import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LayoutNavbar from '@root/components/LayoutNavbar';
import {
  CellTitle,
  CellCheckboxGroup,
  CellCard,
  CellCommon,
} from '@root/components/Cell';
import useUnBindDevices from '@root/hooks/useUnBindDevices';
import Button from '@root/components/Button';
import { IUnbindDevice } from '@root/types/device';
import PageLoading from '@root/components/PageLoading';
import PageLoadError from '@root/components/PageLoadError';
import Empty from '@root/components/Empty';
import SafeAreaInset from '@root/components/SafeAreaInset';
import { ISubscribedDevice } from '@root/types/package';
import PayMethod from '../PayMethod';
import { IPayMethodProps } from '@root/hooks/usePayMethod';

const SelectUnbindDevices = ({
  isHomeIcon,
  isChangePlan,
  children = '',
  maxDevNum = 1,
  selected = [],
  paying = false,
  btnText = '',
  isUnlimited = false,
  btnDisabled = false,
  expandDeviceList = [],
  assignedDevices,
  payMethodState,
  onChange,
  onClickPay,
  renderHeader,
}: {
  isHomeIcon?: boolean;
  isChangePlan?: boolean;
  children?: ReactNode;
  selected?: (number | string)[];
  maxDevNum?: number;
  paying?: boolean;
  isUnlimited?: boolean;
  btnDisabled?: boolean;
  btnText?: string;
  expandDeviceList?: IUnbindDevice[];
  assignedDevices?: ISubscribedDevice[];
  isPayMethod?: boolean;
  payMethodState?: IPayMethodProps;
  onChange?: (v: (number | string)[]) => void;
  onClickPay?: () => void;
  renderHeader?: () => any;
}) => {
  const { t } = useTranslation();
  const { list, loading, getUnBindDevices } = useUnBindDevices();

  const options = useMemo(() => {
    return list.concat(expandDeviceList).map((i) => ({
      title: i.deviceName,
      value: i.deviceId,
    }));
  }, [expandDeviceList, list]);

  if (loading < 1) {
    return (
      <LayoutNavbar
        navbarProps={{
          title: t('select_devices'),
        }}
      >
        <PageLoading />
      </LayoutNavbar>
    );
  }

  if (loading === 2) {
    return (
      <LayoutNavbar
        navbarProps={{
          title: t('select_devices'),
        }}
      >
        <PageLoadError onTryAgain={getUnBindDevices} />
      </LayoutNavbar>
    );
  }

  return (
    <LayoutNavbar
      navbarProps={{
        title: t('select_devices'),
      }}
      containerStyle={{ overflow: 'hidden' }}
      isHomeIcon={isHomeIcon}
    >
      <div className="flex-section-auto">
        {renderHeader && renderHeader()}
        {payMethodState ? <PayMethod {...payMethodState}></PayMethod> : null}
        {assignedDevices && assignedDevices.length > 0 && (
          <>
            <CellTitle>{t('assigned_device')}</CellTitle>
            <CellCard marginTop={0}>
              {assignedDevices.map((i) => (
                <CellCommon inner key={i.id} title={i.name} />
              ))}
            </CellCard>
          </>
        )}
        <CellTitle>
          {isUnlimited
            ? t('choose_device_all')
            : `${t('plan_detail_choose_device')}${
                maxDevNum > 1
                  ? t('device_management_max', { maxDevNum })
                  : t('device_management_max_one', { maxDevNum })
              }`}
        </CellTitle>
        {isChangePlan && (
          <CellTitle paddingTop={0}>
            <p>1. {t('change_plan_select_dev_tips_1')}</p>
            <p style={{ marginTop: 4 }}>
              2. {t('change_plan_select_dev_tips_2')}
            </p>
          </CellTitle>
        )}
        {options.length ? (
          <CellCheckboxGroup
            options={options}
            values={selected}
            onChange={onChange}
            maxLength={isUnlimited ? undefined : maxDevNum}
            marginTop={0}
          />
        ) : (
          <Empty>{t('device_no_valid')}</Empty>
        )}
      </div>
      <div className="flex-section-footer">
        {children}
        <Button disabled={btnDisabled} loading={paying} onClick={onClickPay}>
          {btnText || t('purchase_now')}
        </Button>
        <SafeAreaInset />
      </div>
    </LayoutNavbar>
  );
};

export default SelectUnbindDevices;
